/**
 * Filter bar for the Absence Insights dashboard — a Department › Team ›
 * Subteam cascading multi-select, independent Supervisor/Contract
 * Type/Employee Status multi-selects, a from/to month range, and an
 * absence-type toggle (all / sick leave / child sick leave). Supervisor,
 * Contract Type, and Employee Status are filter-only dimensions (matching
 * the proxy's query) — they narrow the result set but don't cascade against
 * each other or the org hierarchy, unlike Department/Team/Subteam.
 *
 * Deliberately hand-rolled with the same zinc/Tailwind palette already used
 * throughout AbsenceInsightsView.tsx rather than pulling in the
 * design-system's MultiSelect (pearl/cotton palette) — this view is its own
 * small self-contained page, so one consistent look beats mixing two.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { AbsenceType } from '../services/absenceApi';
import { SICK_LEAVE_COLOR, CHILD_CARE_COLOR } from './AbsenceInsightsCharts';

const ABSENCE_TYPE_OPTIONS: { value: AbsenceType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sl', label: 'Sick Leave' },
  { value: 'csl', label: 'Child Sick Leave' },
];

// Matches the export table's actual history floor (int_active_employee_month
// was widened to start 2019-01-01) — a native <input type="month"> lets you
// pick any year in theory, but its year-increment UI is clunky for jumping
// back several years, so Year/Month selects are far more discoverable.
const MIN_DATA_YEAR = 2019;
const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function MonthYearSelect({
  label,
  value,
  onChange,
  disabled = false,
  minValue,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** "YYYY-MM" — earliest selectable value, if any. Years/months before it are omitted from the dropdowns entirely, not just disabled, so it can't be picked even by accident. */
  minValue?: string;
}) {
  const [yearStr, monthStr] = value.split('-');
  const maxYear = new Date().getFullYear();
  const [minYearStr, minMonthStr] = (minValue ?? '').split('-');
  const minYear = minYearStr ? Math.max(Number(minYearStr), MIN_DATA_YEAR) : MIN_DATA_YEAR;
  const minMonth = minYearStr ? Number(minMonthStr) : 1;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  const months = MONTH_ABBREVIATIONS.filter((_, i) => Number(yearStr) > minYear || i + 1 >= minMonth);
  const selectClassName = `h-8 rounded-md border px-1.5 text-[13px] outline-none ${
    disabled
      ? 'cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-400'
      : 'border-zinc-200 bg-white text-zinc-700 focus:border-sky-400'
  }`;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{label}</span>
      <div className="flex gap-1">
        <select
          value={monthStr}
          onChange={(e) => onChange(`${yearStr}-${e.target.value}`)}
          disabled={disabled}
          className={selectClassName}
        >
          {months.map((m) => {
            const i = MONTH_ABBREVIATIONS.indexOf(m);
            return (
              <option key={m} value={String(i + 1).padStart(2, '0')}>
                {m}
              </option>
            );
          })}
        </select>
        <select
          value={yearStr}
          onChange={(e) => onChange(`${e.target.value}-${monthStr}`)}
          disabled={disabled}
          className={selectClassName}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Selected-state styling per absence type, matching the same trustedBlue/
// lightBlue color logic used for Sick Leave/Child Sick Leave in
// MonthlyVolumeChart and TenureChart. "All" stays black/neutral.
const ABSENCE_TYPE_SELECTED_STYLE: Record<AbsenceType, { border: string; background: string; text: string }> = {
  all: { border: '#18181b', background: 'rgba(24,24,27,0.05)', text: '#18181b' }, // zinc-900
  sl: { border: SICK_LEAVE_COLOR, background: hexToRgba(SICK_LEAVE_COLOR, 0.1), text: SICK_LEAVE_COLOR },
  csl: { border: SICK_LEAVE_COLOR, background: CHILD_CARE_COLOR, text: SICK_LEAVE_COLOR },
};

interface MultiSelectDropdownProps {
  label: string;
  placeholder: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Adds a text input at the top of the panel that filters `options` by substring match as you type. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

function MultiSelectDropdown({
  label,
  placeholder,
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder = 'Search…',
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const toggleOption = (option: string) => {
    onChange(selected.includes(option) ? selected.filter((v) => v !== option) : [...selected, option]);
  };

  const triggerLabel =
    selected.length === 0 ? placeholder : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const visibleOptions = searchable
    ? options.filter((opt) => opt.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{label}</span>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => {
            setIsOpen((v) => !v);
            setSearch('');
          }}
          className={`flex h-8 min-w-[9rem] items-center justify-between gap-2 rounded-md border px-2.5 text-[13px] transition-colors ${
            selected.length > 0
              ? 'border-sky-300 bg-sky-50 text-sky-700'
              : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
          }`}
        >
          <span className="truncate">{triggerLabel}</span>
          {selected.length > 0 ? (
            <X
              className="h-3.5 w-3.5 shrink-0 text-sky-500 hover:text-sky-700"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          )}
        </button>

        {isOpen && (
          <div className="absolute left-0 top-full z-20 mt-1 min-w-[13rem] rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
            {searchable && (
              <div className="px-2 pb-1.5">
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-7 w-full rounded-md border border-zinc-200 px-2 text-[13px] text-zinc-700 outline-none focus:border-sky-400"
                />
              </div>
            )}
            <div className="max-h-48 overflow-y-auto">
              {visibleOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-zinc-400">No options</div>
              ) : (
                visibleOptions.map((opt) => (
                  <label
                    key={opt}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] text-zinc-700 hover:bg-zinc-50"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(opt)}
                      onChange={() => toggleOption(opt)}
                      className="h-3.5 w-3.5 accent-sky-500"
                    />
                    <span className="truncate">{opt}</span>
                  </label>
                ))
              )}
            </div>
            <div className="flex justify-between border-t border-zinc-100 px-2.5 pt-1.5">
              <button
                type="button"
                className="text-[11px] font-medium text-sky-600 hover:underline"
                onClick={() => onChange(visibleOptions)}
              >
                All
              </button>
              <button
                type="button"
                className="text-[11px] font-medium text-sky-600 hover:underline"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export interface AbsenceFilterBarProps {
  departmentOptions: string[];
  teamOptions: { department: string; team: string }[];
  subteamOptions: { department: string; team: string; subteam: string }[];
  supervisorOptions: string[];
  contractTypeOptions: string[];
  employeeStatusOptions: string[];
  selectedDepartments: string[];
  selectedTeams: string[];
  selectedSubteams: string[];
  selectedSupervisors: string[];
  selectedContractTypes: string[];
  selectedEmployeeStatuses: string[];
  from: string;
  to: string;
  absenceType: AbsenceType;
  onDepartmentsChange: (next: string[]) => void;
  onTeamsChange: (next: string[]) => void;
  onSubteamsChange: (next: string[]) => void;
  onSupervisorsChange: (next: string[]) => void;
  onContractTypesChange: (next: string[]) => void;
  onEmployeeStatusesChange: (next: string[]) => void;
  onFromChange: (next: string) => void;
  onToChange: (next: string) => void;
  onAbsenceTypeChange: (next: AbsenceType) => void;
  onReset: () => void;
  /** "YYYY-MM" — earliest selectable "From" value, if any. Used on the Employees tab to enforce the 24-month data-retention floor directly in the picker. */
  minFrom?: string;
}

export default function AbsenceFilterBar({
  departmentOptions,
  teamOptions,
  subteamOptions,
  supervisorOptions,
  contractTypeOptions,
  employeeStatusOptions,
  selectedDepartments,
  selectedTeams,
  selectedSubteams,
  selectedSupervisors,
  selectedContractTypes,
  selectedEmployeeStatuses,
  from,
  to,
  absenceType,
  onDepartmentsChange,
  onTeamsChange,
  onSubteamsChange,
  onSupervisorsChange,
  onContractTypesChange,
  onEmployeeStatusesChange,
  onFromChange,
  onToChange,
  onAbsenceTypeChange,
  onReset,
  minFrom,
}: AbsenceFilterBarProps) {
  // Team options narrow to the selected department(s); with none selected, show every team.
  const availableTeams = [...new Set(
    teamOptions
      .filter((t) => selectedDepartments.length === 0 || selectedDepartments.includes(t.department))
      .map((t) => t.team)
  )].sort();

  // Subteam options narrow to the selected department(s) and team(s).
  const availableSubteams = [...new Set(
    subteamOptions
      .filter((s) => selectedDepartments.length === 0 || selectedDepartments.includes(s.department))
      .filter((s) => selectedTeams.length === 0 || selectedTeams.includes(s.team))
      .map((s) => s.subteam)
  )].sort();

  const hasActiveFilters =
    selectedDepartments.length > 0 ||
    selectedTeams.length > 0 ||
    selectedSubteams.length > 0 ||
    selectedSupervisors.length > 0 ||
    selectedContractTypes.length > 0 ||
    selectedEmployeeStatuses.length > 0 ||
    absenceType !== 'all';

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-zinc-200 bg-white p-3">
      <MultiSelectDropdown
        label="Department"
        placeholder="All departments"
        options={departmentOptions}
        selected={selectedDepartments}
        onChange={(next) => {
          onDepartmentsChange(next);
          // Drop any selected team/subteam that no longer belongs to the new department scope.
          const stillValidTeams = new Set(
            teamOptions.filter((t) => next.length === 0 || next.includes(t.department)).map((t) => t.team)
          );
          const nextTeams = selectedTeams.filter((t) => stillValidTeams.has(t));
          onTeamsChange(nextTeams);
          const stillValidSubteams = new Set(
            subteamOptions
              .filter((s) => next.length === 0 || next.includes(s.department))
              .filter((s) => nextTeams.length === 0 || nextTeams.includes(s.team))
              .map((s) => s.subteam)
          );
          onSubteamsChange(selectedSubteams.filter((s) => stillValidSubteams.has(s)));
        }}
      />
      <MultiSelectDropdown
        label="Team"
        placeholder="All teams"
        options={availableTeams}
        selected={selectedTeams}
        onChange={(next) => {
          onTeamsChange(next);
          // Drop any selected subteam that no longer belongs to the new team scope.
          const stillValidSubteams = new Set(
            subteamOptions
              .filter((s) => selectedDepartments.length === 0 || selectedDepartments.includes(s.department))
              .filter((s) => next.length === 0 || next.includes(s.team))
              .map((s) => s.subteam)
          );
          onSubteamsChange(selectedSubteams.filter((s) => stillValidSubteams.has(s)));
        }}
      />
      <MultiSelectDropdown
        label="Subteam"
        placeholder="All subteams"
        options={availableSubteams}
        selected={selectedSubteams}
        onChange={onSubteamsChange}
      />

      <div className="h-8 w-px self-end bg-zinc-200" />

      <MultiSelectDropdown
        label="Supervisor"
        placeholder="All supervisors"
        options={supervisorOptions}
        selected={selectedSupervisors}
        onChange={onSupervisorsChange}
        searchable
        searchPlaceholder="Search supervisors…"
      />
      <MultiSelectDropdown
        label="Contract"
        placeholder="All contracts"
        options={contractTypeOptions}
        selected={selectedContractTypes}
        onChange={onContractTypesChange}
      />
      <MultiSelectDropdown
        label="Status"
        placeholder="All statuses"
        options={employeeStatusOptions}
        selected={selectedEmployeeStatuses}
        onChange={onEmployeeStatusesChange}
      />

      <div className="h-8 w-px self-end bg-zinc-200" />

      <MonthYearSelect label="From" value={from} onChange={onFromChange} minValue={minFrom} />
      <MonthYearSelect label="To" value={to} onChange={onToChange} />

      <div className="h-8 w-px self-end bg-zinc-200" />

      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Absence Type</span>
        <div className="flex gap-1">
          {ABSENCE_TYPE_OPTIONS.map((opt) => {
            const selected = absenceType === opt.value;
            const style = ABSENCE_TYPE_SELECTED_STYLE[opt.value];
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onAbsenceTypeChange(opt.value)}
                className={`rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors ${
                  selected ? '' : 'border-zinc-300 text-zinc-500 hover:border-zinc-400'
                }`}
                style={selected ? { borderColor: style.border, backgroundColor: style.background, color: style.text } : undefined}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={onReset}
          className="ml-auto h-8 self-end rounded-md border border-zinc-200 px-3 text-[12px] font-medium text-zinc-500 hover:border-red-300 hover:text-red-500"
        >
          Reset all
        </button>
      )}
    </div>
  );
}
